import { Component } from '@theme/component';
import { ResizeNotifier } from '@theme/utilities';

/**
 * Read more / read less for product descriptions.
 *
 * line-clamp at rest (correct ellipsis) + max-height only while animating.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} content - Clip container (max-height target)
 * @property {HTMLButtonElement} toggle
 *
 * @extends {Component<Refs>}
 */
export class ProductDescriptionReadMore extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {ResizeNotifier | undefined} */
  #resizeObserver;

  /** @type {MediaQueryList} */
  #desktopQuery = window.matchMedia('(min-width: 750px)');

  /** @type {MediaQueryList} */
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {number} */
  #collapsedHeight = 0;

  connectedCallback() {
    super.connectedCallback();

    this.refs.toggle.addEventListener('click', this.#onToggle);
    this.refs.content.addEventListener('transitionend', this.#onTransitionEnd);
    this.#desktopQuery.addEventListener('change', this.#onBreakpointChange);
    this.#resizeObserver = new ResizeNotifier(() => this.#syncTruncation());
    this.#resizeObserver.observe(this.refs.content);

    this.#initLayout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.toggle.removeEventListener('click', this.#onToggle);
    this.refs.content.removeEventListener('transitionend', this.#onTransitionEnd);
    this.#desktopQuery.removeEventListener('change', this.#onBreakpointChange);
    this.#resizeObserver?.disconnect();
  }

  #getTextElement() {
    return this.refs.content.querySelector('.product-description-read-more__text') ?? this.refs.content;
  }

  #clearClipStyles() {
    const { content: clip } = this.refs;
    clip.style.maxHeight = '';
    clip.style.overflow = '';
  }

  /** Refresh collapsed target height without changing expanded/animating visual state. */
  #updateCollapsedHeight() {
    const { content: clip } = this.refs;
    const hadExpanded = this.hasAttribute('data-expanded');

    if (hadExpanded) {
      this.removeAttribute('data-expanded');
    }

    this.#clearClipStyles();

    if (this.hasAttribute('data-ready')) {
      this.setAttribute('data-truncated', '');
    }

    this.#collapsedHeight = clip.clientHeight;

    if (hadExpanded) {
      this.removeAttribute('data-truncated');
      this.setAttribute('data-expanded', '');
    }
  }

  #onBreakpointChange = () => {
    if (this.hasAttribute('data-expanded') || this.hasAttribute('data-animating')) return;
    this.#remeasureCollapsed();
  };

  /**
   * Measure collapsed height while line-clamp is active, then hand off to line-clamp at rest.
   */
  #initLayout() {
    const { toggle } = this.refs;
    const { needsToggle } = this.#measureCollapsed();

    this.setAttribute('data-ready', '');
    this.toggleAttribute('data-truncated', needsToggle);

    toggle.hidden = !needsToggle;
    toggle.textContent = this.dataset.readMore || 'Read more';
    toggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * Measure with line-clamp (before data-ready, or at rest after clearing inline clip styles).
   *
   * @returns {{ needsToggle: boolean, collapsedHeight: number }}
   */
  #measureCollapsed() {
    const { content: clip } = this.refs;
    const text = this.#getTextElement();

    this.#clearClipStyles();

    // Apply line-clamp for measurement when JS has already initialized
    if (this.hasAttribute('data-ready')) {
      this.setAttribute('data-truncated', '');
    }

    this.#collapsedHeight = clip.clientHeight;
    const needsToggle = text.scrollHeight > this.#collapsedHeight + 1;

    if (!needsToggle) {
      this.removeAttribute('data-truncated');
    }

    return { needsToggle, collapsedHeight: this.#collapsedHeight };
  }

  #remeasureCollapsed() {
    const { toggle } = this.refs;
    const { needsToggle } = this.#measureCollapsed();

    this.toggleAttribute('data-truncated', needsToggle);

    toggle.hidden = !needsToggle;
    toggle.textContent = this.dataset.readMore || 'Read more';
    toggle.setAttribute('aria-expanded', 'false');
  }

  #onToggle = () => {
    if (this.hasAttribute('data-expanded')) {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  #syncTruncation() {
    if (!this.hasAttribute('data-ready')) return;

    const { toggle } = this.refs;

    if (this.hasAttribute('data-expanded') || this.hasAttribute('data-animating')) {
      toggle.hidden = false;
      return;
    }

    this.#remeasureCollapsed();
  }

  #expand() {
    const { content: clip, toggle } = this.refs;
    const readLess = this.dataset.readLess || 'Read less';

    toggle.hidden = false;
    toggle.textContent = readLess;
    toggle.setAttribute('aria-expanded', 'true');

    if (this.#reducedMotion.matches) {
      this.removeAttribute('data-truncated');
      this.setAttribute('data-expanded', '');
      this.#clearClipStyles();
      return;
    }

    const fromHeight = clip.clientHeight;
    clip.style.maxHeight = `${fromHeight}px`;
    clip.style.overflow = 'hidden';
    this.setAttribute('data-animating', '');
    this.removeAttribute('data-truncated');
    this.setAttribute('data-expanded', '');

    const toHeight = clip.scrollHeight;
    // Force a paint of the collapsed height before animating open
    void clip.offsetHeight;
    clip.style.maxHeight = `${toHeight}px`;
  }

  #collapse() {
    const { content: clip, toggle } = this.refs;
    const readMore = this.dataset.readMore || 'Read more';

    toggle.textContent = readMore;
    toggle.setAttribute('aria-expanded', 'false');
    this.#updateCollapsedHeight();

    if (this.#reducedMotion.matches) {
      this.removeAttribute('data-expanded');
      this.#remeasureCollapsed();
      return;
    }

    clip.style.maxHeight = `${clip.scrollHeight}px`;
    clip.style.overflow = 'hidden';
    this.setAttribute('data-animating', '');
    this.removeAttribute('data-expanded');

    // Force a paint of the expanded height before animating closed
    void clip.offsetHeight;
    clip.style.maxHeight = `${this.#collapsedHeight}px`;
  }

  /** @param {TransitionEvent} event */
  #onTransitionEnd = (event) => {
    if (event.propertyName !== 'max-height' || !this.hasAttribute('data-animating')) return;

    const { content: clip } = this.refs;
    this.removeAttribute('data-animating');
    this.#clearClipStyles();

    if (this.hasAttribute('data-expanded')) {
      return;
    }

    this.#remeasureCollapsed();
  };
}

if (!customElements.get('product-description-read-more')) {
  customElements.define('product-description-read-more', ProductDescriptionReadMore);
}
